import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateSimpleInterest, formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const SimpleInterestCalculator = () => {
  const [principal, setPrincipal] = useState(100000);
  const [rate, setRate] = useState(8);
  const [time, setTime] = useState(5);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, rate, time]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const p = Number(principal) || 0;
    const r = Number(rate) || 0;
    const t = Number(time) || 0;
    const siResult = calculateSimpleInterest(p, r, t);
    setResult(siResult);
  };

  const handleDownloadPDF = () => {
    generateGenericPDF('Simple Interest Report', {
      'Principal Amount': principal,
      'Interest Rate': `${rate}% p.a.`,
      'Time Period': `${time} years`,
      'Simple Interest': result.interest,
      'Total Amount': result.totalAmount,
    });
  };

  const handleReset = () => {
    setPrincipal(100000);
    setRate(8);
    setTime(5);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Simple Interest Calculator"
        description="Free Simple Interest Calculator - Calculate SI on principal amount. P×R×T formula with instant results."
        keywords="simple interest calculator, SI calculator, simple interest formula, P×R×T calculator, interest calculator"
        canonicalPath="/simple-interest"
      />
      <div className="calculator-header">
        <h1>Simple Interest Calculator</h1>
        <p>Calculate simple interest on your principal amount</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Principal Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                min="1000"
                max="100000000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="10000000"
                step="1000"
                value={principal || 0}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((principal || 0) / 10000000) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={rate === '' ? '' : rate}
              onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="0.1"
              max="50"
              step="0.1"
            />
            <div className="slider-container">
              <input
                type="range"
                min="0.1"
                max="30"
                step="0.1"
                value={rate || 0}
                onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((rate || 0) / 30) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Time Period (Years)</label>
            <input
              type="number"
              value={time === '' ? '' : time}
              onChange={(e) => setTime(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="50"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="30"
                step="1"
                value={time || 0}
                onChange={(e) => setTime(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((time || 0) / 30) * 100}%` }}
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>Total Amount</h3>
                <div className="amount">{formatCurrency(result.totalAmount)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Amount</span>
                  <span className="value">{formatCurrency(result.principal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Simple Interest</span>
                  <span className="value" style={{ color: '#10b981' }}>
                    +{formatCurrency(result.interest)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Interest per Year</span>
                  <span className="value">{formatCurrency(result.interest / time)}</span>
                </div>
              </div>

              <div className="info-box" style={{ marginTop: '20px', background: '#f0fdf4', borderColor: '#86efac' }}>
                <h4>Calculation</h4>
                <p style={{ fontFamily: 'monospace', marginTop: '8px' }}>
                  SI = P × R × T / 100
                </p>
                <p style={{ marginTop: '8px' }}>
                  SI = {formatCurrency(principal)} × {rate}% × {time} / 100
                </p>
                <p style={{ marginTop: '4px' }}>
                  SI = {formatCurrency(result.interest)}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About Simple Interest</h4>
        <p>
          Simple Interest is calculated only on the principal amount. It remains constant throughout 
          the investment period. The formula is:
        </p>
        <p style={{ marginTop: '8px', fontFamily: 'monospace', fontSize: '16px' }}>
          Simple Interest = (P × R × T) / 100
        </p>
        <p style={{ marginTop: '8px' }}>
          Where P = Principal, R = Rate of interest per annum, T = Time in years
        </p>
      </div>
    </div>
  );
};

export default SimpleInterestCalculator;
